import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Loader2, Bot, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { startChat, sendChatMessage } from "@/lib/api";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import ResultScreen from "@/components/ResultScreen";

const Avatar = () => (
  <div className="shrink-0 h-9 w-9 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center shadow-md">
    <Bot className="h-5 w-5 text-white" />
  </div>
);

const formatMd = (t) =>
  DOMPurify.sanitize(
    String(t).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>"),
    { ALLOWED_TAGS: ["strong"], ALLOWED_ATTR: [] }
  );

const BUBBLE_MOTION = { initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.25 } };

const Bubble = ({ role, children }) => {
  const isUser = role === "user";
  return (
    <motion.div {...BUBBLE_MOTION}
      className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      {!isUser && <div className="mr-2 mt-0.5"><Avatar /></div>}
      <div data-testid={`chat-bubble-${role}`}
        className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap
          ${isUser
            ? "bg-accent text-white rounded-br-md"
            : "bg-card border border-border text-primary rounded-bl-md shadow-sm"}`}
        dangerouslySetInnerHTML={{ __html: formatMd(children) }} /> {/* formatMd = DOMPurify.sanitize(markdown) */}
    </motion.div>
  );
};

export default function Chat({ assistantName = "Assistente Leadders", source = "leadders", accent = "accent" }) {
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [options, setOptions] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const scrollRef = useRef(null);
  const startedRef = useRef(false);
  const answers = messages.filter((m) => m.role === "user").length;
  const progress = Math.min(95, Math.round((answers / 11) * 100));

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    (async () => {
      setLoading(true);
      try {
        const data = await startChat(source);
        setSessionId(data.session_id);
        setMessages([{ id: crypto.randomUUID(), role: "assistant", content: data.message }]);
        setOptions(data.options || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Impossibile avviare l'assistente");
      } finally {
        setLoading(false);
      }
    })();
  }, [source]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading, options]);

  const send = async (override) => {
    const text = (override ?? input).trim();
    if (!text || loading || !sessionId) return;
    setInput("");
    setOptions([]);
    setMessages((m) => [...m, { id: crypto.randomUUID(), role: "user", content: text }]);
    setLoading(true);
    try {
      const data = await sendChatMessage(sessionId, text);
      setMessages((m) => [...m, { id: crypto.randomUUID(), role: "assistant", content: data.message }]);
      setOptions(data.options || []);
      if (data.completed && data.result) {
        setTimeout(() => setResult(data.result), 600);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore di comunicazione");
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    return <ResultScreen result={result} onRestart={() => window.location.reload()} />;
  }

  return (
    <div className="w-full max-w-2xl mx-auto">
      <div className="bg-background sm:rounded-2xl rounded-xl border border-border shadow-xl shadow-slate-200/40 overflow-hidden flex flex-col h-[min(540px,calc(100dvh-6rem))] sm:h-[640px]">
        {/* header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border bg-card">
          <Avatar />
          <div className="flex-1">
            <div className="font-display font-semibold text-primary" data-testid="chat-assistant-name">{assistantName}</div>
            <div className="flex items-center gap-1.5 text-xs text-emerald-600">
              <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" /> Online · risponde in tempo reale
            </div>
          </div>
          <ShieldCheck className="h-5 w-5 text-muted-foreground" />
        </div>
        {/* progress */}
        <div className="px-5 py-2 bg-card border-b border-border flex items-center gap-3" data-testid="chat-progress">
          <div className="flex-1 h-1.5 rounded-full bg-secondary overflow-hidden">
            <div className="h-full bg-accent transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
          <span className="text-[11px] font-medium text-muted-foreground whitespace-nowrap">
            {answers === 0 ? "Pochi tocchi, 2 minuti" : `${progress}% completato`}
          </span>
        </div>

        {/* messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 sm:p-5 space-y-4 bg-secondary/30">
          <AnimatePresence initial={false}>
            {messages.map((m) => <Bubble key={m.id} role={m.role}>{m.content}</Bubble>)}
          </AnimatePresence>
          {loading && (
            <div className="flex justify-start">
              <div className="mr-2"><Avatar /></div>
              <div className="bg-card border border-border rounded-2xl rounded-bl-md px-4 py-3 flex gap-1">
                <span className="h-2 w-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="h-2 w-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="h-2 w-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          )}

          {/* quick-reply chips */}
          {!loading && options.length > 0 && (
            <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
              className="flex flex-wrap gap-2 pt-1 pl-0 sm:pl-11">
              {options.map((o, i) => (
                <button key={o} data-testid={`chat-option-${i}`} onClick={() => send(o)}
                  className="px-4 py-2.5 min-h-[44px] rounded-full text-sm font-medium border-2 border-accent/40 text-accent
                    bg-accent/5 hover:bg-accent hover:text-white hover:border-accent active:scale-95 transition-all duration-200">
                  {o}
                </button>
              ))}
            </motion.div>
          )}
        </div>

        {/* input */}
        <div className="p-3 sm:p-4 border-t border-border bg-card" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
          <div className="flex items-center gap-2">
            <Input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={options.length ? "Oppure scrivi qui..." : "Scrivi la tua risposta..."} disabled={loading || !sessionId}
              className="flex-1 h-11 text-base" />
            <Button data-testid="chat-send-button" onClick={() => send()} disabled={loading || !input.trim()}
              size="icon" className="bg-accent hover:bg-accent/90 text-white shrink-0">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2 text-center">
            Dati trattati nel rispetto del GDPR UE 2016/679
          </p>
        </div>
      </div>
    </div>
  );
}
